import { For, type JSX, Show } from "solid-js";
import { cn } from "@stockpilot/shared";

/**
 * Lightweight, theme-aware, dependency-free charts for Stockpilot analytics.
 *
 * Rendered with semantic HTML + a touch of inline SVG so they:
 *  - inherit theme tokens (`--primary`, `--warning`, …) automatically,
 *  - stay accessible (each chart exposes a hidden data table via aria),
 *  - never cause horizontal scroll (everything is fluid width).
 *
 * No external charting library — this is deliberate (bundle size + theming).
 */

export interface ChartDatum {
  label: string;
  value: number;
  /**
   * Optional tailwind text-color class driving the series color (e.g.
   * "text-primary"). Defaults to the primary accent.
   */
  colorClass?: string;
  /** Optional formatted value shown to the right of the bar. */
  display?: string;
}

interface BarChartProps {
  data: ChartDatum[];
  /** Accessible name for the chart. */
  ariaLabel: string;
  class?: string;
  /** Max number of bars to render (rest are dropped). */
  max?: number;
}

/**
 * Horizontal bar chart — ideal for "stock by warehouse", "value by product",
 * etc. Bars animate width in via a CSS transition. Fully responsive.
 */
export function BarChart(props: BarChartProps): JSX.Element {
  const rows = () => {
    const sliced = typeof props.max === "number" ? props.data.slice(0, props.max) : props.data;
    return sliced;
  };
  const peak = () => Math.max(1, ...rows().map((d) => Math.abs(d.value)));

  return (
    <div class={cn("w-full", props.class)} role="img" aria-label={props.ariaLabel}>
      <Show
        when={rows().length > 0}
        fallback={<p class="text-sm text-muted-foreground">{props.ariaLabel}</p>}
      >
        <ul class="flex flex-col gap-3">
          <For each={rows()}>
            {(d) => {
              const pct = () => Math.round((Math.abs(d.value) / peak()) * 100);
              const color = d.colorClass ?? "text-primary";
              return (
                <li class="flex flex-col gap-1">
                  <div class="flex items-baseline justify-between gap-3 text-sm">
                    <span class="min-w-0 flex-1 truncate text-foreground/90">{d.label}</span>
                    <span class="shrink-0 font-mono text-xs font-semibold tabular-nums text-muted-foreground">
                      {d.display ?? d.value}
                    </span>
                  </div>
                  <div class="h-2.5 w-full overflow-hidden rounded-full bg-muted/40">
                    <div
                      class={cn(
                        "h-full rounded-full bg-current transition-[width] duration-700 ease-out",
                        color,
                      )}
                      style={{ width: `${Math.max(pct(), 2)}%` }}
                    />
                  </div>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>
    </div>
  );
}

interface DonutSegment {
  label: string;
  value: number;
  /** CSS color for the arc, e.g. "hsl(var(--success))". */
  color: string;
}

interface DonutChartProps {
  segments: DonutSegment[];
  ariaLabel: string;
  /** Big number shown in the center. */
  centerValue?: string;
  /** Small caption under the center value. */
  centerCaption?: string;
  class?: string;
}

/**
 * Donut chart for compositional breakdowns (e.g. healthy vs low vs out of
 * stock). Pure SVG stroke-dasharray arcs — theme colors passed in by caller.
 */
export function DonutChart(props: DonutChartProps): JSX.Element {
  const RADIUS = 42;
  const CIRC = 2 * Math.PI * RADIUS;
  const total = () => props.segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);

  // Precompute cumulative offsets for each arc.
  const arcs = () => {
    const t = total();
    if (t <= 0) {
      return [];
    }
    let acc = 0;
    const result: { color: string; dash: number; offset: number }[] = [];
    for (const s of props.segments) {
      if (s.value <= 0) {
        continue;
      }
      const frac = s.value / t;
      result.push({ color: s.color, dash: frac * CIRC, offset: -acc * CIRC });
      acc += frac;
    }
    return result;
  };

  return (
    <div class={cn("flex items-center gap-5", props.class)} role="img" aria-label={props.ariaLabel}>
      <div class="relative shrink-0">
        <svg viewBox="0 0 100 100" class="h-28 w-28 -rotate-90">
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke="hsl(var(--muted))"
            stroke-width="11"
            opacity="0.35"
          />
          <For each={arcs()}>
            {(arc) => (
              <circle
                cx="50"
                cy="50"
                r={RADIUS}
                fill="none"
                stroke={arc.color}
                stroke-width="11"
                stroke-linecap="round"
                stroke-dasharray={`${arc.dash} ${CIRC - arc.dash}`}
                stroke-dashoffset={arc.offset}
              />
            )}
          </For>
        </svg>
        <Show when={props.centerValue}>
          <div class="absolute inset-0 flex flex-col items-center justify-center">
            <span class="text-xl font-bold tabular-nums leading-none">{props.centerValue}</span>
            <Show when={props.centerCaption}>
              <span class="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                {props.centerCaption}
              </span>
            </Show>
          </div>
        </Show>
      </div>
      <ul class="flex min-w-0 flex-1 flex-col gap-1.5 text-sm">
        <For each={props.segments}>
          {(s) => (
            <li class="flex items-center gap-2">
              <span
                class="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ "background-color": s.color }}
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1 truncate text-foreground/85">{s.label}</span>
              <span class="shrink-0 font-mono text-xs font-semibold tabular-nums text-muted-foreground">
                {s.value}
              </span>
            </li>
          )}
        </For>
      </ul>
    </div>
  );
}
